import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { createSelector } from "@reduxjs/toolkit";
import { Link, useParams } from "react-router-dom";
import classNames from "classnames";
import { getConfig } from "@edx/frontend-platform";
import { injectIntl } from "@edx/frontend-platform/i18n";

import SubNav from "./SubNav";
import { ReactComponent as Overview } from "./svg/overview.svg";
import { ReactComponent as Progress } from "./svg/progress.svg";
import { levelIcons } from "../../../config/icons";
import { Icon } from "@openedx/paragon";
import { Close } from "@openedx/paragon/icons";
import { ReactComponent as Menu } from "./svg/overview.svg";
import { useModel } from "../../../generic/model-store";
import useIsSpeechcraftMFE from "../../../hooks/useIsSpeechcraftMFE";
import { useMobileResponsive, useOutsideClick } from "../../../utils";
import messages from "./messages";
import { Role } from "../../../config";
import { getLocalizedFormattedNumber } from "../../../utils/i18n";

const currentCourseTitleSelector = createSelector(
  (state) => state.models.coursewareMeta || {},
  (state) => state.courseware.courseId,
  (coursesById, courseId) => coursesById[courseId]?.title
);

const getCurrentCourseSectionsSelector = createSelector(
  (state) => state?.models?.sections || {},
  (state) => state.courseware.courseId,
  (sections, courseId) =>
    Object.keys(sections).reduce((acc, sectionId) => {
      if (sections[sectionId].courseId === courseId) {
        return { ...acc, [sectionId]: sections[sectionId] };
      }

      return acc;
    }, {})
);
function Sidebar({ intl, courseId, sequenceId, section }) {
  const isSpeechcraftMFE = useIsSpeechcraftMFE();
  const { locale = 'en' } = useSelector((state) => state.app);

  const courseTitle = useSelector(currentCourseTitleSelector);
  const courseSections = useSelector(getCurrentCourseSectionsSelector) || {};
  const rolesData = useSelector((state) => state.app.roles);
  const courseLevels = useSelector((state) => state?.models?.levels) || {};
  const courseProgress = useModel("courseProgress", courseId);

  const isSpeechcrafter =
    isSpeechcraftMFE &&
    !!rolesData?.[0]?.roles?.find((role) => role.position_code === Role.speechcrafter);

  const { order: sectionLevel } = courseLevels[section?.id] || {};
  const SectionIcon = levelIcons[sectionLevel];

  const ref = useRef(null);
  useOutsideClick(() => {
    setIsToggleSidebar(false);
  }, ref);

  const { title: sectionTitle } = section || {};
  const courseSubsectionsIds = Object.keys(courseSections).reduce(
    (acc, sectionId) => {
      const { sequenceIds } = courseSections[sectionId];
      return sequenceIds.includes(sequenceId) ? [...acc, ...sequenceIds] : acc;
    },
    []
  );

  const { unitId: unitIdFromUrl, sequenceId: sequenceIdFromUrl } = useParams();

  const [toggleSidebar, setIsToggleSidebar] = useState(false);

  const handleSidebar = () => {
    setIsToggleSidebar(!toggleSidebar);
  };

  useEffect(() => {
    const [element] = document.getElementsByTagName("body");
    if (toggleSidebar) {
      element.classList.add("show-courses-sidebar");
    } else {
      element.classList.remove("show-courses-sidebar");
    }
  }, [toggleSidebar]);

  const isMobileView = useMobileResponsive(991);
  useEffect(() => {
    if (!isMobileView) {
      setIsToggleSidebar(false);
    }
  }, [isMobileView]);

  useEffect(() => {
    if (isMobileView) {
      setIsToggleSidebar(false);
    }
  }, [isMobileView, unitIdFromUrl]);

  const [activeSection, setActiveSection] = useState("");

  useEffect(() => {
    setActiveSection(sequenceId);
  }, [sequenceId]);

  return (
    <>
      <div
        className={classNames("mb-sidebar-overlay", {
          "show-overlay": toggleSidebar,
        })}
      ></div>
      <div ref={ref}>
        <div className="d-lg-none w-100">
          <div className="row">
            <div className="col-12 sidebar-toggle-btn-wrapper">
              <button
                onClick={handleSidebar}
                aria-expanded={toggleSidebar ? "true" : "false"}
              >
                <Icon src={toggleSidebar ? Close : Menu} />{" "}
                {intl.formatMessage(messages.overviewButtonText)}
              </button>
            </div>
          </div>
        </div>

        <div
          className={classNames("sidebarContainer", {
            "show-mb-sidebar": toggleSidebar,
          })}
        >
          <aside className="w-100" aria-label="Sidebar">
            <div className="w-100">
              <div className="course-overview">
                <div className="d-lg-none d-flex mb-4">
                  <button
                    aria-label="Close Sidebar"
                    className="reset-btn"
                    onClick={handleSidebar}
                  >
                    <Icon src={Close} />
                  </button>
                </div>

                <div className="course-title">{courseTitle}</div>
                {!isSpeechcraftMFE && (
                  <div className="course-progress">
                    <Progress aria-hidden="true" />
                    {intl.formatMessage(messages.courseProgress)}
                    <span>
                      {intl.formatMessage(messages.courseProgressComplete, {
                        progress: courseProgress?.progress
                          ? getLocalizedFormattedNumber(Math.round(courseProgress?.progress), locale)
                          : getLocalizedFormattedNumber(0, locale),
                      })}
                    </span>
                  </div>
                )}
              </div>

              <div className="external-links mb-4">
                <ul>
                  <li>
                    <Link
                      to={`/course/${courseId}/home`}
                      className="btn overview-title d-flex align-items-center justify-content-start"
                    >
                      <Overview />
                      <span>
                        {intl.formatMessage(messages.courseOverviewLinkText)}
                      </span>
                    </Link>
                  </li>
                  {isSpeechcrafter && (
                    <li>
                      <Link
                        to={`/course/${courseId}/coordinator-tracking`}
                        className="btn overview-title d-flex align-items-center justify-content-start"
                      >
                        <Progress />
                        <span>
                          {intl.formatMessage(messages.courseTrackingLinkText)}
                        </span>
                      </Link>
                    </li>
                  )}
                </ul>
              </div>

              <div className="course-section-nav">
                <div className="px-4 py-3 course-section-title">
                  {SectionIcon && <SectionIcon aria-hidden="true" />}
                  {sectionTitle}
                </div>
                <>
                  {courseSubsectionsIds.map((subsectionsId) => (
                    <SubNav
                      subsectionsId={subsectionsId}
                      courseId={courseId}
                      activeSection={activeSection}
                      setActiveSection={setActiveSection}
                    />
                  ))}
                </>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}

export default injectIntl(Sidebar);
